import { useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { ehCelular, emApp, guardarFuncaoPendente } from '@/lib/app';
import { regraFaixaInstalar } from '@/lib/portao';
import { faixaFechada, fecharFaixa, useConviteNaTela } from '@/lib/faixaApp';
import FaixaInstalar from './FaixaInstalar';

// ---------------------------------------------------------------------------
// Instalar o app é SUGESTÃO, nunca portão.
// Uma regra só decide onde a faixa aparece (ver src/lib/portao.ts):
//   /carteira/:codigo · /meus-ingressos · /minhas-inscricoes · / (após 1 tela)
// Em todas as outras telas — compra, Pix, evento, painel, caixa, portaria,
// barraca, talão, entrar/senha — não aparece nada.
// Na carteira, se o cartão "Coloque na tela do celular" está visível, a faixa
// também não aparece: nunca dois convites na mesma tela.
// ---------------------------------------------------------------------------
export default function PortaoApp({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const noCelularSemApp = ehCelular() && !emApp();

  const regra = regraFaixaInstalar(pathname);
  const conviteOcupado = useConviteNaTela();

  const [aberta, setAberta] = useState(() => !faixaFechada());
  const [rolouUmaTela, setRolouUmaTela] = useState(false);

  // Guarda o token do link de função para o app oferecer depois ("Entrar numa função").
  useEffect(() => {
    if (!noCelularSemApp) return;
    const m = pathname.match(/^\/equipe\/([A-Za-z0-9_-]+)/);
    if (m) guardarFuncaoPendente(m[1]);
  }, [noCelularSemApp, pathname]);

  // Início: a faixa só entra depois de rolar uma tela cheia.
  useEffect(() => {
    if (regra !== 'depois-de-rolar') {
      setRolouUmaTela(false);
      return;
    }
    const aoRolar = () => setRolouUmaTela(window.scrollY > window.innerHeight);
    aoRolar();
    window.addEventListener('scroll', aoRolar, { passive: true });
    return () => window.removeEventListener('scroll', aoRolar);
  }, [regra, pathname]);

  // Computador ou app instalado: exatamente a tela, sem nada por cima.
  if (!noCelularSemApp) return <>{children}</>;

  const mostrar =
    regra !== 'nunca' &&
    aberta &&
    !conviteOcupado &&
    (regra === 'agora' || rolouUmaTela);

  function agoraNao() {
    fecharFaixa();
    setAberta(false);
  }

  return (
    <>
      {mostrar && (
        <div className={regra === 'depois-de-rolar' ? 'fixed inset-x-0 top-0 z-[92]' : undefined}>
          <FaixaInstalar aoFechar={agoraNao} />
        </div>
      )}
      {children}
    </>
  );
}